// src/components/Dashboard.js

import React from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Syringe,
  Clock,
  Calendar,
  ArrowRight
} from 'lucide-react';
import './Dashboard.css';

// Small circular progress ring used on the Completion Rate card.
const ProgressRing = ({ percent }) => {
  const radius = 26;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <svg width="64" height="64" viewBox="0 0 64 64" className="dash-progress-ring">
      <circle
        cx="32" cy="32" r={radius}
        className="dash-progress-ring-track"
        strokeWidth="6"
        fill="none"
      />
      <circle
        cx="32" cy="32" r={radius}
        className="dash-progress-ring-value"
        strokeWidth="6"
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 32 32)"
      />
    </svg>
  );
};

const Dashboard = ({ patients, onViewPatient, onNavigate }) => {
  const totalPatients = patients?.length || 0;

  const totalVaccinations = patients?.reduce((total, patient) =>
    total + (patient.vaccines?.length || 0), 0
  ) || 0;

  const pendingDoses = patients?.reduce((total, patient) =>
    total + (patient.vaccines?.filter(vaccine => vaccine.status === 'pending')?.length || 0), 0
  ) || 0;

  const patientsWithVaccines = patients?.filter(p => p.vaccines?.length > 0) || [];
  const completionRate = totalPatients > 0
    ? Math.round((patientsWithVaccines.length / totalPatients) * 100)
    : 0;

  const recentPatients = patients?.slice(0, 3) || [];

  const allVaccinations = patients?.flatMap(patient =>
    (patient.vaccines || []).map(vaccine => ({
      ...vaccine,
      patientName: patient.name,
      patientId: patient.id
    }))
  ) || [];

  const recentVaccinations = allVaccinations
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  const statCardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: 'easeOut' } },
    hover: { y: -4, transition: { duration: 0.2 } }
  };

  const handleViewAllPatients = () => {
    if (onNavigate) onNavigate('patients');
  };

  const handlePatientClick = (patient) => {
    if (onViewPatient) onViewPatient(patient);
  };

  const todayFormatted = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  return (
    <motion.div
      className="dashboard"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div className="dash-header" variants={itemVariants}>
        <div className="dash-header-text">
          <span className="dash-eyebrow">Clinic Overview</span>
          <h1>Dashboard</h1>
          <p>Welcome back — here's today's vaccination status at a glance.</p>
        </div>
        <div className="dash-date-badge">
          <Calendar size={16} />
          <span>{todayFormatted}</span>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <motion.div className="dash-stats-grid" variants={containerVariants}>
        <motion.div
          className="dash-stat-card dash-stat-card--patients"
          variants={statCardVariants}
          initial="hidden"
          animate="visible"
          whileHover="hover"
          onClick={handleViewAllPatients}
          role="button"
          tabIndex={0}
        >
          <div className="dash-stat-icon dash-stat-icon--patients">
            <Users size={22} />
          </div>
          <div className="dash-stat-content">
            <h3>{totalPatients}</h3>
            <p>Total Patients</p>
            <span className="dash-stat-subtitle">Registered in system</span>
          </div>
        </motion.div>

        <motion.div
          className="dash-stat-card dash-stat-card--vaccinations"
          variants={statCardVariants}
          initial="hidden"
          animate="visible"
          whileHover="hover"
        >
          <div className="dash-stat-icon dash-stat-icon--vaccinations">
            <Syringe size={22} />
          </div>
          <div className="dash-stat-content">
            <h3>{totalVaccinations}</h3>
            <p>Total Vaccinations</p>
            <span className="dash-stat-subtitle">Doses administered</span>
          </div>
        </motion.div>

        <motion.div
          className="dash-stat-card dash-stat-card--pending"
          variants={statCardVariants}
          initial="hidden"
          animate="visible"
          whileHover="hover"
        >
          <div className="dash-stat-icon dash-stat-icon--pending">
            <Clock size={22} />
          </div>
          <div className="dash-stat-content">
            <h3>{pendingDoses}</h3>
            <p>Pending Doses</p>
            <span className="dash-stat-subtitle">Awaiting administration</span>
          </div>
        </motion.div>

        <motion.div
          className="dash-stat-card dash-stat-card--completion"
          variants={statCardVariants}
          initial="hidden"
          animate="visible"
          whileHover="hover"
        >
          <ProgressRing percent={completionRate} />
          <div className="dash-stat-content">
            <h3>{completionRate}%</h3>
            <p>Completion Rate</p>
            <span className="dash-stat-subtitle">Vaccination progress</span>
          </div>
        </motion.div>
      </motion.div>

      {/* Recent Activity Section */}
      <motion.div className="dash-recent-activity" variants={containerVariants}>
        {/* Recent Patients */}
        <motion.div className="dash-activity-section" variants={itemVariants}>
          <div className="dash-section-header">
            <h3>Recent Patients</h3>
            <motion.button
              className="dash-view-all-btn"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleViewAllPatients}
            >
              View All <ArrowRight size={14} />
            </motion.button>
          </div>
          <div className="dash-activity-card">
            {recentPatients.length > 0 ? (
              <div className="dash-activity-list">
                {recentPatients.map((patient, index) => (
                  <motion.div
                    key={patient.id}
                    className="dash-activity-item"
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.08 }}
                    onClick={() => handlePatientClick(patient)}
                  >
                    <div className="dash-patient-avatar">
                      {patient.name?.split(' ').map(n => n[0]).join('') || 'P'}
                    </div>
                    <div className="dash-patient-details">
                      <strong>{patient.name || 'Unknown Patient'}</strong>
                      <span className="font-mono">{patient.mrn || 'No MRN'}</span>
                    </div>
                    <div className="dash-vaccine-count">
                      {patient.vaccines?.length || 0} dose{(patient.vaccines?.length || 0) !== 1 ? 's' : ''}
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="dash-empty-state">
                <Users size={28} />
                <p>No patients registered</p>
                <span className="dash-empty-subtitle">Add your first patient to get started</span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Recent Vaccinations */}
        <motion.div className="dash-activity-section" variants={itemVariants}>
          <div className="dash-section-header">
            <h3>Recent Vaccinations</h3>
            <motion.button
              className="dash-view-all-btn"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleViewAllPatients}
            >
              View All <ArrowRight size={14} />
            </motion.button>
          </div>
          <div className="dash-activity-card">
            {recentVaccinations.length > 0 ? (
              <div className="dash-activity-list">
                {recentVaccinations.map((vaccine, index) => (
                  <motion.div
                    key={`${vaccine.patientId}-${vaccine.id}`}
                    className="dash-activity-item"
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.08 }}
                    onClick={() => {
                      const patient = patients.find(p => p.id === vaccine.patientId);
                      if (patient && onViewPatient) onViewPatient(patient);
                    }}
                  >
                    <div className={`dash-vaccine-badge dash-vaccine-badge--${vaccine.status || 'completed'}`}>
                      <Syringe size={15} />
                    </div>
                    <div className="dash-vaccine-details">
                      <strong>{vaccine.name || 'Unknown Vaccine'}</strong>
                      <span>{vaccine.patientName || 'Unknown Patient'} · {vaccine.dose || 'Dose'}</span>
                    </div>
                    <div className="dash-vaccine-date">
                      <span className="font-mono">
                        {vaccine.date ? new Date(vaccine.date).toLocaleDateString() : 'No date'}
                      </span>
                      <div className={`dash-status-indicator dash-status-indicator--${vaccine.status || 'completed'}`}>
                        {vaccine.status || 'completed'}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="dash-empty-state">
                <Syringe size={28} />
                <p>No vaccinations recorded</p>
                <span className="dash-empty-subtitle">Add vaccines to see them here</span>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* Quick Actions */}
      {totalPatients === 0 && (
        <motion.div
          className="dash-quick-actions"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="dash-action-card">
            <h3>Get Started</h3>
            <p>Add your first patient to start tracking vaccines</p>
            <motion.button
              className="dash-action-btn dash-action-btn--primary"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => onNavigate && onNavigate('patients')}
            >
              <Users size={18} />
              Add First Patient
            </motion.button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Dashboard;